import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
 Bot,
 Send,
 X,
 Sparkles,
 ArrowUpRight,
 ThumbsUp,
 ThumbsDown,
 Trash2,
} from "lucide-react";
import type { SidebarRole } from "./Sidebar";
import { CHATBOT_OPEN_EVENT } from "../services/chatbot";

/* ============================================================
 * WebileAssist — floating, role-aware help bot (v2).
 *
 * Pure rule-based: every message is scored against a curated
 * knowledge base by keyword overlap + token-overlap fallback.
 * No external calls, no AI. The KB, greeting and colours all
 * swap on`role`so an admin and an employee get different bots.
 *
 * v2 enhancements:
 * - Typing indicator with animated dots
 * - Smooth message entry animations
 * - Context-aware quick actions (navigate buttons)
 * - Thumbs up / down feedback on bot replies
 * - Session-persistent conversation history
 * - Categorised quick-reply chips
 * - Animated floating open button with unread pulse
 * - Current-page awareness for smarter suggestions
 * ========================================================== */

const BRAND ="WebileAssist";
const STORAGE_KEY ="webileassist_history";
const TYPING_DELAY_MS = 600; // simulated typing delay

interface ChatbotProps {
 role: SidebarRole;
 userId: string;
}

interface Rule {
 id: string;
 topic: string;
 keywords: string[];
 answer: string;
 /** Optional quick-action navigations embedded in the reply */
 actions?: Array<{ label: string; path: string }>;
}

interface ChatMessage {
 id: string;
 from: "bot" | "user";
 text: string;
 timestamp: number;
 feedback?:"up" | "down"| null;
 actions?: Array<{ label: string; path: string }>;
}

/* ---------- Brand theme ---------- */
const BRAND_THEME = {
 chipBg: "bg-emerald-50",
 chipTxt: "text-role-primary",
 chipRing: "ring-emerald-200",
 sendBg: "bg-gradient-brand hover:shadow-md",
 bubble: "bg-emerald-50",
 bubbleRing: "ring-emerald-200",
 bubbleText: "text-gray-800",
};

const THEME: Record<
 SidebarRole,
 {
 tagline: string;
 greet: string;
 grad: string;
 chipBg: string;
 chipTxt: string;
 chipRing: string;
 sendBg: string;
 bubble: string;
 bubbleRing: string;
 bubbleText: string;
 }
> = {
 admin: {
 tagline: "for admins",
 greet: "Hi! I'm **WebileAssist** — your in-app guide for managing projects, users, timesheets and audits. Ask me anything, type`help`to see all topics, or pick a chip below.",
 grad: "from-emerald-500 to-teal-500",
 ...BRAND_THEME,
 },
 manager: {
 tagline: "for managers",
 greet: "Hello! I'm **WebileAssist** — I'll help you with approvals, your projects, timesheets and team reports. Type`help`to browse topics, or pick a chip.",
 grad: "from-emerald-500 to-teal-500",
 ...BRAND_THEME,
 },
 employee: {
 tagline: "for the team",
 greet: "Hey! I'm **WebileAssist** — your work buddy. I can show you how to log work, submit timesheets, apply for leave, find tasks and more. Type`help`to see everything.",
 grad: "from-emerald-500 to-teal-500",
 ...BRAND_THEME,
 },
};

/* ---------- Common rules (small-talk + cross-role help) ---------- */
const COMMON_RULES: Rule[] = [
 {
 id: "greeting",
 topic: "Small talk",
 keywords: ["hi","hello","hey","howdy","yo","greetings","hola","namaste",
 ],
 answer: "Hello! 👋 I'm here to help. Type`help`to see everything I know, or pick a chip below.",
 },
 {
 id: "morning",
 topic: "Small talk",
 keywords: ["good morning","morning"],
 answer: "Good morning! ☀️ Hope you have a productive day. What can I help you with?",
 },
 {
 id: "afternoon",
 topic: "Small talk",
 keywords: ["good afternoon","afternoon"],
 answer: "Good afternoon! Halfway there. Need a hand with anything?",
 },
 {
 id: "evening",
 topic: "Small talk",
 keywords: ["good evening","evening"],
 answer: "Good evening! Wrapping up the day? I can help you check your timesheets or submit a quick work log.",
 },
 {
 id: "night",
 topic: "Small talk",
 keywords: ["good night","night","gn"],
 answer: "Good night! 🌙 Don't forget to submit today's timesheet before you sign off.",
 },
 {
 id: "how-are-you",
 topic: "Small talk",
 keywords: ["how are you","how r u","how's it going","hows it going","what's up","whats up","sup",
 ],
 answer: "Doing great — and ready to help! Got a question?",
 },
 {
 id: "thanks",
 topic: "Small talk",
 keywords: ["thank","thanks","ty","thx","appreciate","cheers"],
 answer: "You're welcome! 🙌 Anything else I can help with?",
 },
 {
 id: "ok",
 topic: "Small talk",
 keywords: ["ok","okay","alright","fine","got it","sure","cool"],
 answer: "👍 Let me know if you need anything else.",
 },
 {
 id: "bye",
 topic: "Small talk",
 keywords: ["bye","goodbye","see you","see ya","cya","later","gtg"],
 answer: "Bye for now! 👋 Just click the bot icon again whenever you need help.",
 },
 {
 id: "yes",
 topic: "Small talk",
 keywords: ["yes","yeah","yep","yup","yess"],
 answer: "Great — what would you like to know? Pick a chip below or type your question.",
 },
 {
 id: "no",
 topic: "Small talk",
 keywords: ["no","nope","nah"],
 answer: "No problem. I'll stay quiet — just open me again if you need a hand.",
 },
 {
 id: "joke",
 topic: "Small talk",
 keywords: ["joke","funny","lol","haha"],
 answer: "Why don't programmers like nature? It has too many bugs. 🐛 Anyway — back to work, anything I can help with?",
 },
 {
 id: "who",
 topic: "About the bot",
 keywords: ["who are you","what are you","your name","about you","bot name","are you ai","are you human",
 ],
 answer: "I'm **WebileAssist** — a rule-based helper baked into Time Tracker. I match your message against a curated knowledge base and reply instantly. No AI, no data leaves your browser.",
 },
 {
 id: "abilities",
 topic: "About the bot",
 keywords: ["what can you do","your features","your skills","what do you know","abilities","capabilities",
 ],
 answer: "I can answer questions about most Time Tracker flows — projects, users, work logs, timesheets, leaves, tasks, issues, approvals, calendar, audits, migrations and more. Type`help`for the full topic list.",
 },
 {
 id: "about-app",
 topic: "About the app",
 keywords: ["what is time tracker","what is this app","about the app","tell me about","what is webile","what is this tool",
 ],
 answer: "Time Tracker is your team's hub for projects, tasks, work logs, timesheets, leaves and approvals — all role-aware. Admins manage everything; managers approve and review; employees log work and request leaves.",
 },
 {
 id: "signout",
 topic: "Account",
 keywords: ["sign out","signout","log out","logout","exit app","quit","log off",
 ],
 answer: "Go to your **Profile** page and use the **Sign out** button. Your session token is cleared and you'll return to the login page.",
 actions: [],
 },
 {
 id: "password",
 topic: "Account",
 keywords: ["password","reset password","forgot","recover","change password","new password",
 ],
 answer: "If you're logged in, go to **Settings** to change your password. If you're locked out, click **Reset your password?** on the login page — a reset link will be emailed to you (expires in 30 minutes).",
 actions: [{ label: "Open Settings", path: "/settings"}],
 },
 {
 id: "who-am-i",
 topic: "Account",
 keywords: ["who am i","my account","my profile","my role","what's my role","whats my role","my id",
 ],
 answer: "Your role and name show in the sidebar header. Visit your **Profile** page for full account details.",
 actions: [],
 },
 {
 id: "settings",
 topic: "Account",
 keywords: ["settings","preferences","appearance","theme","dark mode","light mode",
 ],
 answer: "Open **Settings** from the sidebar to change your display preferences, theme, and notification options.",
 actions: [{ label: "Open Settings", path: "/settings"}],
 },
 {
 id: "support",
 topic: "Support",
 keywords: ["support","contact","help me","issue","problem","bug","stuck","not working","broken","error",
 ],
 answer: "For app issues, click **Report a bug** in the sidebar to submit details directly to your admin. Many pages also have a **Retry / Back** button if a load fails.",
 },
 {
 id: "shortcut",
 topic: "Tips",
 keywords: ["shortcut","keyboard","hotkey","tip","tips","tricks"],
 answer: "A few tips:\n• Most lists have a **search bar** in their header\n• **Status pills** are clickable filters\n• The **Export** button on dashboards downloads the current view as Excel\n• Use the **floating bot icon** (bottom-right) anytime for quick help",
 },
 {
 id: "time",
 topic: "Now",
 keywords: ["what time","time now","current time","what's the time","whats the time",
 ],
 answer: "", // computed at runtime
 },
 {
 id: "date",
 topic: "Now",
 keywords: ["what date","today","what day","current date","what's the date","whats the date",
 ],
 answer: "", // computed at runtime
 },
 {
 id: "navigation",
 topic: "Navigation",
 keywords: ["navigate","go to","open page","where is","find page","take me to",
 ],
 answer:'Just tell me what you\'re looking for! For example:\n•"How do I submit a work log?"\n•"Where are timesheets?"\n•"Open projects"\nI\'ll point you to the right page.',
 },
];

/* ---------- Admin-only ---------- */
const ADMIN_RULES: Rule[] = [
 {
 id: "admin-dashboard",
 topic: "Dashboard",
 keywords: ["dashboard","kpi","metrics","stats","overview","summary","snapshot","home",
 ],
 answer: "Your admin **Dashboard** shows KPI stats, attention-required tiles, portfolio health and quick-action cards.",
 actions: [{ label: "Open Dashboard", path: "/dashboard"}],
 },
 {
 id: "create-project",
 topic: "Projects",
 keywords: ["create project","new project","add project","make project","start project","begin project","set up project",
 ],
 answer: "Go to **Projects** and click **+ New project**, or jump directly to the create page.",
 actions: [
 { label: "All Projects", path: "/projects"},
 { label: "Create Project", path: "/createproject"},
 ],
 },
 {
 id: "edit-project",
 topic: "Projects",
 keywords: ["edit project","update project","change project","modify project",
 ],
 answer: "Open the project from **All Projects**. The detail page shows status, members and lets you update fields.",
 actions: [{ label: "All Projects", path: "/projects"}],
 },
 {
 id: "delete-project",
 topic: "Projects",
 keywords: ["delete project","remove project","archive project"],
 answer: "Projects are **never deleted** — they're marked **Completed** instead. Open the project and click the green **Mark as Completed** button.",
 actions: [{ label: "All Projects", path: "/projects"}],
 },
 {
 id: "mark-completed",
 topic: "Projects",
 keywords: ["mark completed","complete project","close project","finish project","completion",
 ],
 answer: "Open a project's detail page, then click the green **Mark as Completed** button in the hero section.",
 actions: [{ label: "All Projects", path: "/projects"}],
 },
 {
 id: "bulk-project",
 topic: "Projects",
 keywords: ["bulk","import project","upload project","excel project","csv project","many projects",
 ],
 answer: "Use the **Bulk upload** feature to import many projects at once from an Excel/CSV file.",
 actions: [{ label: "Bulk Upload Projects", path: "/projects/bulk-upload"}],
 },
 {
 id: "all-projects",
 topic: "Projects",
 keywords: ["all project","project list","see project","view project","open project",
 ],
 answer: "**Sidebar → Projects** lists all projects with status indicators and search.",
 actions: [{ label: "All Projects", path: "/projects"}],
 },
 {
 id: "add-user",
 topic: "Users",
 keywords: ["add employee","new employee","create employee","hire","onboard","add user","new user","create user",
 ],
 answer: "Go to **Users** and click **+ Add employee**, or jump directly to the form.",
 actions: [
 { label: "All Users", path: "/users"},
 { label: "Add Employee", path: "/admin/employees/new"},
 ],
 },
 {
 id: "all-users",
 topic: "Users",
 keywords: ["all employee","employee list","see employee","list of employee","view employee","list users","all users","user list",
 ],
 answer: "**Sidebar → Users** shows all users with role badges and search.",
 actions: [{ label: "All Users", path: "/users"}],
 },
 {
 id: "bulk-users",
 topic: "Users",
 keywords: ["bulk user","import user","upload user","excel user","csv user","many users",
 ],
 answer: "Use **Bulk upload** to import many users from an Excel/CSV file.",
 actions: [{ label: "Bulk Upload Users", path: "/admin/users/bulk-upload"}],
 },
 {
 id: "assign-manager",
 topic: "Assignments",
 keywords: ["assign manager","manager to project","project manager","make manager","set manager",
 ],
 answer: "Open the **Assign Manager** page to link a manager to a project.",
 actions: [{ label: "Assign Manager", path: "/admin/assign-manager"}],
 },
 {
 id: "assign-employee",
 topic: "Assignments",
 keywords: ["assign employee","employee to project","project employee","add to project","put on project",
 ],
 answer: "Open the **Assign Employee** page to link employees to projects.",
 actions: [{ label: "Assign Employee", path: "/admin/assign-employee"}],
 },
 {
 id: "audit",
 topic: "Audits",
 keywords: ["audit","audit log","history","track activity","who changed","who did","trail","log of changes",
 ],
 answer: "**Audit logs** track all user actions. Filter by user, action type and date range.",
 actions: [{ label: "Audit Logs", path: "/admin/audit-logs"}],
 },
 {
 id: "admin-worklogs",
 topic: "Work logs",
 keywords: ["work log","worklog","time log","time tracking","see hours","all hours","logged hours",
 ],
 answer: "The **Worklogs** page shows every submitted work log across all employees.",
 actions: [{ label: "All Worklogs", path: "/admin/worklogs"}],
 },
 {
 id: "admin-bulk-worklogs",
 topic: "Work logs",
 keywords: ["bulk worklog","import worklog","upload worklog","excel worklog",
 ],
 answer: "Use **Bulk upload** to import many work logs from an Excel/CSV.",
 actions: [
 { label: "Bulk Upload Worklogs", path: "/admin/worklogs/bulk-upload"},
 ],
 },
 {
 id: "timesheets",
 topic: "Timesheets",
 keywords: ["timesheet","timesheets","time sheet","time sheets","weekly sheet",
 ],
 answer: "**Timesheets** show structured weekly/monthly time entries for all employees.",
 actions: [{ label: "Timesheets", path: "/timesheets"}],
 },
 {
 id: "timesheet-import ",
 topic: "Timesheets",
 keywords: ["import timesheet","upload timesheet","timesheet bulk","timesheet excel",
 ],
 answer: "Use the **Import Timesheets** feature to bulk-upload timesheet data.",
 actions: [{ label: "Import Timesheets", path: "/admin/timesheets/import "}],
 },
 {
 id: "leaves",
 topic: "Leaves",
 keywords: ["leave","leaves","all leaves","leave management"],
 answer: "The **Leaves** page shows all leave requests across the organization.",
 actions: [{ label: "Leaves", path: "/leaves"}],
 },
 {
 id: "migrations",
 topic: "Migrations",
 keywords: ["migration","migrations","data migration","import export","move data",
 ],
 answer: "The **Migrations** page helps you import/export data between systems.",
 actions: [{ label: "Migrations", path: "/admin/migrations"}],
 },
 {
 id: "admin-settings",
 topic: "Settings",
 keywords: ["admin settings","portal setup","configure","admin config"],
 answer: "Use **Admin Settings** to configure portal roles, modules, and access controls.",
 actions: [
 { label: "Admin Settings", path: "/admin/settings"},
 { label: "Portal Setup", path: "/admin/setup"},
 ],
 },
 {
 id: "client",
 topic: "Projects",
 keywords: ["client","company","customer","add client"],
 answer: "Clients are auto-created when you create a project — just type the **client name** in the form. Existing clients are matched by name.",
 },
 {
 id: "bugs",
 topic: "Bugs",
 keywords: ["bug report","reported bugs","all bugs","bug list"],
 answer: "View all reported bugs from the **Bugs** management page.",
 actions: [{ label: "All Bugs", path: "/admin/bugs"}],
 },
];

/* ---------- Manager-only ---------- */
const MANAGER_RULES: Rule[] = [
 {
 id: "manager-dashboard",
 topic: "Dashboard",
 keywords: ["dashboard","home","overview","summary"],
 answer: "Your **Dashboard** shows pending approvals, team activity, and project status at a glance.",
 actions: [{ label: "Open Dashboard", path: "/dashboard"}],
 },
 {
 id: "pending-worklogs",
 topic: "Approvals",
 keywords: ["pending work","approve work","review work","worklog approval","pending worklog","review hours",
 ],
 answer: "**Pending work logs** shows submissions awaiting your review. Each row has Approve / Reject buttons.",
 actions: [{ label: "Pending Work Logs", path: "/worklogs/pending"}],
 },
 {
 id: "pending-leaves",
 topic: "Approvals",
 keywords: ["pending leave","approve leave","leave approval","leave request","review leave",
 ],
 answer: "**Pending leaves** shows leave requests awaiting your approval.",
 actions: [{ label: "Pending Leaves", path: "/leaves/pending"}],
 },
 {
 id: "reject",
 topic: "Approvals",
 keywords: ["reject","deny","decline","send back"],
 answer: "On the pending list page, each row has a **Reject** button. Add a short reason in the modal — the employee will see it in their work-log / leave detail.",
 },
 {
 id: "my-projects",
 topic: "Projects",
 keywords: ["my project","projects i manage","assigned project","my portfolio","all projects",
 ],
 answer: "**Projects** lists every project where you're assigned as a manager.",
 actions: [{ label: "My Projects", path: "/projects"}],
 },
 {
 id: "timesheets",
 topic: "Timesheets",
 keywords: ["timesheet","timesheets","time sheet","weekly sheet","approve timesheet",
 ],
 answer: "**Timesheets** shows all team timesheets — review, approve or send back.",
 actions: [{ label: "Timesheets", path: "/timesheets"}],
 },
 {
 id: "team-worklogs",
 topic: "Insights",
 keywords: ["team work","all work logs","team hours","employee hours","their hours","people's hours","time logs",
 ],
 answer: "**Team work logs** shows all logged hours across your team members.",
 actions: [{ label: "Team Time Logs", path: "/manager/timelogs"}],
 },
 {
 id: "issues",
 topic: "Issues",
 keywords: ["issue","blocker","raised by employee","team issue","team blocker",
 ],
 answer: "**Issues** shows all blockers raised by your team members.",
 actions: [{ label: "Team Issues", path: "/manager/issues"}],
 },
 {
 id: "post-task",
 topic: "Tasks",
 keywords: ["post task","create task","assign task","new task","give task","add task",
 ],
 answer: "Open a project from **Projects** → go to **Tasks** tab → click **+ New task**.",
 actions: [{ label: "My Projects", path: "/projects"}],
 },
 {
 id: "bulk-tasks",
 topic: "Tasks",
 keywords: ["bulk task","upload task","import task","many task","task excel",
 ],
 answer: "Inside a project's **Tasks** screen, click **Bulk upload tasks** to import from Excel/CSV.",
 },
 {
 id: "leaves",
 topic: "Leaves",
 keywords: ["leave","all leaves","leave list","my leaves","apply leave"],
 answer: "View all leaves or apply for your own from the **Leaves** page.",
 actions: [
 { label: "All Leaves", path: "/leaves"},
 { label: "Apply for Leave", path: "/leaves/new"},
 ],
 },
 {
 id: "migrations",
 topic: "Import/Export",
 keywords: ["import ","export","migration","bulk","excel"],
 answer: "Use the **Import/Export** page for bulk data operations.",
 actions: [{ label: "Import/Export", path: "/manager/import-export"}],
 },
];

/* ---------- Employee-only ---------- */
const EMPLOYEE_RULES: Rule[] = [
 {
 id: "emp-dashboard",
 topic: "Dashboard",
 keywords: ["dashboard","home","overview","summary"],
 answer: "Your **Dashboard** shows your recent activity, pending items, and quick actions.",
 actions: [{ label: "Open Dashboard", path: "/dashboard"}],
 },
 {
 id: "submit-worklog",
 topic: "Work logs",
 keywords: ["submit work","log work","log time","submit hours","post work log","new work log","record work","track time",
 ],
 answer: "Go to **Submit work log** — pick the task, set the date and minutes, then click submit.",
 actions: [{ label: "Submit Work Log", path: "/worklogs/new"}],
 },
 {
 id: "my-worklogs",
 topic: "Work logs",
 keywords: ["my work log","see my work","previous work","past hours","my hours","what i logged",
 ],
 answer: "**My work logs** shows every log you've submitted, with status pills (pending / approved / rejected).",
 actions: [{ label: "My Worklogs", path: "/employees/{userId}/worklogs"}],
 },
 {
 id: "rejected-worklog",
 topic: "Work logs",
 keywords: ["rejected","why rejected","denied","manager comment","feedback",
 ],
 answer: "Click any rejected log in **My work logs** — the detail page shows your manager's comments. You can edit and resubmit.",
 },
 {
 id: "timesheets",
 topic: "Timesheets",
 keywords: ["timesheet","timesheets","time sheet","weekly sheet","fill timesheet","submit timesheet",
 ],
 answer: "**Timesheets** shows your weekly/monthly time entries. Submit and track approval status here.",
 actions: [{ label: "My Timesheets", path: "/timesheets"}],
 },
 {
 id: "calendar",
 topic: "Calendar",
 keywords: ["calendar","weekly view","schedule","week","visual","see week","graphical","work calendar",
 ],
 answer: "**Work calendar** opens a Monday–Sunday grid of every approved session. Longer sessions render bolder; overlaps cascade so each block is visible.",
 actions: [
 { label: "Work Calendar", path: "/employees/{userId}/work-calendar"},
 ],
 },
 {
 id: "apply-leave",
 topic: "Leaves",
 keywords: ["apply leave","request leave","vacation","time off","ask for leave","take a day","holiday","off day",
 ],
 answer: "Go to **Apply for leave** — pick dates and reason, then submit. Your manager gets a pending request.",
 actions: [{ label: "Apply for Leave", path: "/leaves/new"}],
 },
 {
 id: "my-leaves",
 topic: "Leaves",
 keywords: ["my leave","leave status","past leave","balance","leave history","all leaves",
 ],
 answer: "**Leaves** shows every leave you've requested with its current status.",
 actions: [{ label: "My Leaves", path: "/leaves"}],
 },
 {
 id: "tasks",
 topic: "Tasks",
 keywords: ["my task","what to work on","open task","assigned task","todo","my todo",
 ],
 answer: "**My tasks** lists every task assigned to you. Click one to see full details.",
 actions: [{ label: "My Tasks", path: "/employees/{userId}/tasks"}],
 },
 {
 id: "weekly-log",
 topic: "Tasks",
 keywords: ["weekly log","fill week","5 day","week grid","log for week"],
 answer: "**Weekly log** opens a grid where you can fill an entire week's hours per task at once.",
 actions: [{ label: "Weekly Log", path: "/employees/{userId}/weeklylog"}],
 },
 {
 id: "raise-issue",
 topic: "Issues",
 keywords: ["raise issue","report problem","blocker","i'm stuck","im stuck","flag issue","my issues",
 ],
 answer: "Go to **My issues** to raise a blocker against a task — your manager will see it in their Issues board.",
 actions: [{ label: "My Issues", path: "/employees/{userId}/issues"}],
 },
 {
 id: "projects",
 topic: "Projects",
 keywords: ["my project","projects","assigned projects","project list"],
 answer: "**Projects** shows all projects you're assigned to.",
 actions: [{ label: "My Projects", path: "/projects"}],
 },
];

/* ---------- Quick-reply suggestions per role (categorized) ---------- */
interface ChipCategory {
 label: string;
 chips: string[];
}

const QUICK_REPLIES: Record<SidebarRole, ChipCategory[]> = {
 admin: [
 {
 label: "Common",
 chips: ["Show my dashboard","Where are timesheets?","How do I add a user?",
 ],
 },
 {
 label: "Projects",
 chips: ["How do I create a project?","Bulk upload projects"],
 },
 {
 label: "Operations",
 chips: ["Where are audit logs?","Show all worklogs"],
 },
 ],
 manager: [
 {
 label: "Common",
 chips: ["Show my dashboard","Where are timesheets?","Approve a work log",
 ],
 },
 {
 label: "Approvals",
 chips: ["Pending work logs","Pending leaves"],
 },
 {
 label: "Team",
 chips: ["Team hours","Team issues"],
 },
 ],
 employee: [
 {
 label: "Common",
 chips: ["Submit a work log","Open my timesheets","Apply for leave"],
 },
 {
 label: "Work",
 chips: ["My tasks","Weekly log","Work calendar"],
 },
 {
 label: "Help",
 chips: ["Where are my worklogs?","Raise an issue"],
 },
 ],
};

/* ---------- Rule lookup ---------- */
function rulesFor(role: SidebarRole): Rule[] {
 if (role ==="admin") return [...COMMON_RULES, ...ADMIN_RULES];
 if (role ==="manager") return [...COMMON_RULES, ...MANAGER_RULES];
 return [...COMMON_RULES, ...EMPLOYEE_RULES];
}

const STOPWORDS = new Set(["the","a","an","is","are","was","were","be","to","of","in","on","for","and","or","i","me","my","you","your","we","do","does","did","how","what","where","when","why","can","could","would","should","please","pls","plz",
]);

function tokenize(s: string): string[] {
 return s
 .toLowerCase()
 .split(/[^a-z0-9']+/)
 .filter((t) => t && !STOPWORDS.has(t));
}

function scoreRule(message: string, rule: Rule): number {
 const msgTokens = new Set(tokenize(message));
 let score = 0;
 for (const kw of rule.keywords) {
 if (message.includes(kw)) {
 score += kw.includes("") ? 3 : 1;
 continue;
 }
 if (kw.includes("")) {
 const kwTokens = kw.split(/\s+/).filter((t) => !STOPWORDS.has(t));
 if (kwTokens.length >= 2 && kwTokens.every((t) => msgTokens.has(t))) {
 score += 2;
 }
 }
 }
 return score;
}

interface MatchResult {
 best: Rule | null;
 ranked: Array<{ rule: Rule; score: number }>;
}

function matchRules(question: string, rules: Rule[]): MatchResult {
 const q = question.toLowerCase().trim();
 if (!q) return { best: null, ranked: [] };

 const ranked = rules
 .map((r) => ({ rule: r, score: scoreRule(q, r) }))
 .filter((x) => x.score > 0)
 .sort((a, b) => b.score - a.score);

 return { best: ranked[0]?.rule ?? null, ranked };
}

/* ---------- Dynamic answers ---------- */
function dynamicAnswer(ruleId: string): string | null {
 if (ruleId ==="time") {
 return`It's **${new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit"})}** on your device.`;
 }
 if (ruleId ==="date") {
 return`Today is **${new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric"})}**.`;
 }
 return null;
}

/* ---------- Topic list (`help`/`topics`) ---------- */
function topicsAnswer(role: SidebarRole): string {
 const rules = rulesFor(role).filter((r) => r.topic !=="Small talk");
 const byTopic = new Map<string, string[]>();
 for (const r of rules) {
 const list = byTopic.get(r.topic) ?? [];
 const example = r.keywords[0] ?? r.id;
 if (!list.includes(example)) list.push(example);
 byTopic.set(r.topic, list);
 }
 const lines: string[] = [`Here's what I can help with as a **${role}**:`];
 for (const [topic, examples] of byTopic) {
 lines.push(`• **${topic}** — try: _"${examples.slice(0, 3).join('","')}"_`,
 );
 }
 lines.push("\nType any of those phrases, or just describe what you want to do.",
 );
 return lines.join("\n");
}

function isTopicsCommand(s: string): boolean {
 const q = s
 .toLowerCase()
 .trim()
 .replace(/[?.!]+$/,"");
 return (
 q ==="help"||
 q ==="topics"||
 q ==="menu"||
 q ==="list"||
 q ==="what can you do"||
 q ==="show topics");
}

/* ---------- Path resolver (replaces {userId} tokens) ---------- */
function resolvePath(path: string, userId: string): string {
 return path.replace("{userId}", userId);
}

/* ---------- Typing indicator ---------- */
function TypingIndicator() {
 return (
 <div className="flex items-start gap-2">
 <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-white shadow-sm">
 <Bot className="h-3.5 w-3.5"/>
 </span>
 <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm bg-emerald-50 px-4 py-3 shadow-sm ring-1 ring-inset ring-emerald-200">
 <span
 className="typing-dot h-2 w-2 rounded-full bg-slate-400"style={{ animationDelay: "0ms"}}
 />
 <span
 className="typing-dot h-2 w-2 rounded-full bg-slate-400"style={{ animationDelay: "150ms"}}
 />
 <span
 className="typing-dot h-2 w-2 rounded-full bg-slate-400"style={{ animationDelay: "300ms"}}
 />
 </div>
 </div>
 );
}

/* ---------- Tiny markdown-ish renderer ---------- */
function RichText({
 text,
 onLink,
}: {
 text: string;
 onLink: (path: string) => void;
}) {
 const TOKEN = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|_[^_]+_|`[^`]+`)/g;
 const lines = text.split("\n");

 return (
 <span>
 {lines.map((line, lineIdx) => {
 const parts = line.split(TOKEN).filter((p) => p !=="");
 return (
 <span key={lineIdx}>
 {parts.map((part, i) => {
 const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
 if (link) {
 const [, label, href] = link;
 const isExternal = /^https?:\/\//.test(href!);
 return (
 <button
 key={i}
 type="button"onClick={() => {
 if (!href) return;
 if (isExternal)
 window.open(href,"_blank","noopener,noreferrer");
 else onLink(href);
 }}
 className="inline-flex items-center gap-0.5 font-semibold text-role-primary underline decoration-dotted underline-offset-2 transition hover:opacity-80">
 {label}
 <ArrowUpRight className="h-3 w-3"/>
 </button>
 );
 }
 const bold = /^\*\*([^*]+)\*\*$/.exec(part);
 if (bold)
 return (
 <strong key={i} className="font-semibold">
 {bold[1]}
 </strong>
 );
 const italic = /^_([^_]+)_$/.exec(part);
 if (italic)
 return (
 <em key={i} className="italic text-slate-600">
 {italic[1]}
 </em>
 );
 const code = /^`([^`]+)`$/.exec(part);
 if (code)
 return (
 <code
 key={i}
 className="rounded bg-slate-100 px-1 py-0.5 text-[11px] font-mono text-slate-700">
 {code[1]}
 </code>
 );
 return <span key={i}>{part}</span>;
 })}
 {lineIdx < lines.length - 1 && <br />}
 </span>
 );
 })}
 </span>
 );
}

/* ---------- Quick Action Buttons ---------- */
function QuickActions({
 actions,
 onNavigate,
}: {
 actions: Array<{ label: string; path: string }>;
 onNavigate: (path: string) => void;
}) {
 if (!actions || actions.length === 0) return null;
 return (
 <div className="mt-2 flex flex-wrap gap-1.5">
 {actions.map((action) => (
 <button
 key={action.path}
 type="button"onClick={() => onNavigate(action.path)}
 className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-role-primary transition hover:bg-emerald-50 hover:shadow-sm active:scale-95">
 <ArrowUpRight className="h-3 w-3"/>
 {action.label}
 </button>
 ))}
 </div>
 );
}

/* ---------- Component ---------- */
export default function Chatbot({ role, userId }: ChatbotProps) {
 const navigate = useNavigate();
 const location = useLocation();
 const theme = THEME[role];
 const rules = useMemo(() => rulesFor(role), [role]);

 const [open, setOpen] = useState(false);
 const [typing, setTyping] = useState(false);
 const [chipCategory, setChipCategory] = useState(0);

 // Load conversation from session storage
 const loadMessages = useCallback((): ChatMessage[] => {
 try {
 const stored = sessionStorage.getItem(`${STORAGE_KEY}_${role}`);
 if (stored) {
 const parsed = JSON.parse(stored) as ChatMessage[];
 if (Array.isArray(parsed) && parsed.length > 0) return parsed;
 }
 } catch {
 /* ignore */
 }
 return [
 { id: "m-0", from: "bot", text: theme.greet, timestamp: Date.now() },
 ];
 }, [role, theme.greet]);

 const [messages, setMessages] = useState<ChatMessage[]>(loadMessages);
 const [draft, setDraft] = useState("");
 const scrollRef = useRef<HTMLDivElement | null>(null);
 const inputRef = useRef<HTMLInputElement | null>(null);

 // Persist messages to sessionStorage
 useEffect(() => {
 try {
 sessionStorage.setItem(`${STORAGE_KEY}_${role}`,
 JSON.stringify(messages),
 );
 } catch {
 /* quota exceeded, ignore */
 }
 }, [messages, role]);

 // Reset on role change
 useEffect(() => {
 setMessages(loadMessages());
 setDraft("");
 }, [role, loadMessages]);

 // Listen for external toggle event (from Navbar help button)
 useEffect(() => {
 const handler = () => {
 setOpen((prev) => !prev);
 };
 window.addEventListener(CHATBOT_OPEN_EVENT, handler);
 return () => window.removeEventListener(CHATBOT_OPEN_EVENT, handler);
 }, []);

 // Auto-scroll
 useEffect(() => {
 const el = scrollRef.current;
 if (el) el.scrollTop = el.scrollHeight;
 }, [messages, open, typing]);

 // Focus input when opened
 useEffect(() => {
 if (open) {
 requestAnimationFrame(() => inputRef.current?.focus());
 }
 }, [open]);

 const buildReply = (
 question: string,
 ): { text: string; actions?: Array<{ label: string; path: string }> } => {
 const trimmed = question.trim();
 if (!trimmed) return { text: "Say something and I'll do my best to help!"};

 if (isTopicsCommand(trimmed)) return { text: topicsAnswer(role) };

 const { best } = matchRules(trimmed, rules);
 if (best) {
 const answer = dynamicAnswer(best.id) ?? best.answer;
 return { text: answer, actions: best.actions };
 }

 const msgTokens = new Set(tokenize(trimmed));
 const soft = rules
 .filter((r) => r.topic !=="Small talk")
 .map((r) => {
 const tokens = r.keywords.flatMap((k) => tokenize(k));
 const overlap = tokens.filter((t) => msgTokens.has(t)).length;
 return { rule: r, overlap };
 })
 .filter((x) => x.overlap > 0)
 .sort((a, b) => b.overlap - a.overlap)
 .slice(0, 3);

 if (soft.length > 0) {
 const lines = ["I'm not 100% sure I got that — did you mean one of these?",
 ...soft.map(
 ({ rule }) =>`• _"${rule.keywords[0]}"_ — _${rule.topic}_`,
 ),"","Or type`help`to see every topic I cover.",
 ];
 return { text: lines.join("\n") };
 }

 return {
 text: ["Hmm, I don't have an answer for that one. Try: ","• Typing`help`to see every topic I cover","• Picking one of the quick chips below","• Describing what you want to do in different words",
 ].join("\n"),
 };
 };

 const idRef = useRef(0);

 const ask = (text: string) => {
 const trimmed = text.trim();
 if (!trimmed) return;
 idRef.current += 1;
 const id = idRef.current;
 const userMsg: ChatMessage = {
 id:`u-${id}`,
 from: "user",
 text: trimmed,
 timestamp: Date.now(),
 };
 setMessages((prev) => [...prev, userMsg]);
 setDraft("");
 setTyping(true);

 // Simulate typing delay for natural feel
 setTimeout(() => {
 const { text: replyText, actions } = buildReply(trimmed);
 const reply: ChatMessage = {
 id:`b-${id}`,
 from: "bot",
 text: replyText,
 timestamp: Date.now(),
 actions,
 };
 setMessages((prev) => [...prev, reply]);
 setTyping(false);
 }, TYPING_DELAY_MS);
 };

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 ask(draft);
 };

 const handleChip = (text: string) => ask(text);

 const handleLink = (path: string) => {
 const resolved = resolvePath(path, userId);
 setOpen(false);
 navigate(resolved);
 };

 const handleFeedback = (msgId: string, feedback: "up" | "down") => {
 setMessages((prev) =>
 prev.map((m) =>
 m.id === msgId
 ? { ...m, feedback: m.feedback === feedback ? null : feedback }
 : m,
 ),
 );
 };

 const clearHistory = () => {
 const greeting: ChatMessage = {
 id: "m-0",
 from: "bot",
 text: theme.greet,
 timestamp: Date.now(),
 };
 setMessages([greeting]);
 sessionStorage.removeItem(`${STORAGE_KEY}_${role}`);
 };

 // Context-aware greeting based on current page
 const getPageHint = (): string | null => {
 const path = location.pathname;
 if (path.includes("/projects"))
 return"I see you're on the Projects page. Need help managing projects?";
 if (path.includes("/timesheets"))
 return"Working on timesheets? I can help with submissions and approvals.";
 if (path.includes("/worklogs"))
 return"On the work logs page — need help submitting or reviewing?";
 if (path.includes("/leaves"))
 return"On the leaves page — want to apply or check status?";
 if (path.includes("/dashboard"))
 return"On your dashboard — want a quick overview of what I can help with?";
 return null;
 };

 const chips = QUICK_REPLIES[role];
 const currentChips = chips[chipCategory] ?? chips[0];

 return (
 <>
 {/* CSS for animations */}
 <style>{`@keyframes typing-bounce {
 0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
 30% { transform: translateY(-4px); opacity: 1; }
 }
 .typing-dot {
 animation: typing-bounce 1.4s infinite ease-in-out;
 }
 @keyframes msg-enter {
 from { opacity: 0; transform: translateY(8px) scale(0.97); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 .msg-animate {
 animation: msg-enter 0.25s ease-out forwards;
 }
 @keyframes float-in {
 from { opacity: 0; transform: translateY(16px) scale(0.9); }
 to { opacity: 1; transform: translateY(0) scale(1); }
 }
 .chat-float-in {
 animation: float-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
 }`}</style>

 {/* Chat panel */}
 {open && (
 <div
 role="dialog"aria-label={BRAND}
 className="chat-float-in fixed bottom-20 right-5 z-[60] flex w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20"style={{ height: "min(600px, calc(100vh - 7rem))"}}
 >
 {/* Header */}
 <div className="relative overflow-hidden bg-gradient-brand px-4 py-3 text-white">
 <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/15 blur-2xl"/>
 <div className="pointer-events-none absolute -left-4 -bottom-6 h-16 w-16 rounded-full bg-white/10 blur-xl"/>
 <div className="relative flex items-center gap-3">
 <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 ring-1 ring-inset ring-white/30 backdrop-blur">
 <Bot className="h-5 w-5"/>
 </span>
 <div className="min-w-0 flex-1">
 <p className="truncate text-sm font-bold leading-tight">
 {BRAND}
 </p>
 <p className="flex items-center gap-1.5 text-[11px] font-medium text-white/85">
 <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-sm shadow-emerald-300"/>
 Online · {theme.tagline}
 </p>
 </div>
 <div className="flex items-center gap-1">
 <button
 type="button"onClick={clearHistory}
 aria-label="Clear conversation"title="Clear conversation"className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/15 hover:text-white">
 <Trash2 className="h-3.5 w-3.5"/>
 </button>
 <button
 type="button"onClick={() => setOpen(false)}
 aria-label="Close chat"className="rounded-lg p-1.5 text-white/90 transition hover:bg-white/15">
 <X className="h-4 w-4"/>
 </button>
 </div>
 </div>
 </div>

 {/* Context hint bar */}
 {getPageHint() && messages.length <= 2 && (
 <div className="border-b border-slate-100 bg-blue-50/50 px-3 py-2">
 <p className="text-[11px] text-slate-600">💡 {getPageHint()}</p>
 </div>
 )}

 {/* Messages */}
 <div
 ref={scrollRef}
 className="flex-1 space-y-2.5 overflow-y-auto bg-slate-50/40 px-3 py-3">
 {messages.map((m) => {
 if (m.from ==="user") {
 return (
 <div key={m.id} className="msg-animate flex justify-end">
 <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-slate-900 px-3.5 py-2.5 text-xs leading-relaxed text-white shadow-sm">
 {m.text}
 </div>
 </div>
 );
 }
 return (
 <div key={m.id} className="msg-animate flex items-start gap-2">
 <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-brand text-white shadow-sm">
 <Bot className="h-3.5 w-3.5"/>
 </span>
 <div className="max-w-[82%]">
 <div
 className={`whitespace-pre-line rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-xs leading-relaxed shadow-sm ring-1 ring-inset ${theme.bubble} ${theme.bubbleText} ${theme.bubbleRing}`}
 >
 <RichText text={m.text} onLink={handleLink} />
 </div>
 {/* Quick action buttons */}
 {m.actions && m.actions.length > 0 && (
 <QuickActions
 actions={m.actions.map((a) => ({
 ...a,
 path: resolvePath(a.path, userId),
 }))}
 onNavigate={handleLink}
 />
 )}
 {/* Feedback buttons */}
 {m.id !=="m-0"&& (
 <div className="mt-1 flex items-center gap-1">
 <button
 type="button"onClick={() => handleFeedback(m.id,"up")}
 aria-label="Helpful"className={`rounded p-1 transition ${
 m.feedback ==="up"?"bg-emerald-100 text-role-primary":"text-slate-300 hover:bg-slate-100 hover:text-slate-500"}`}
 >
 <ThumbsUp className="h-3 w-3"/>
 </button>
 <button
 type="button"onClick={() => handleFeedback(m.id,"down")}
 aria-label="Not helpful"className={`rounded p-1 transition ${
 m.feedback ==="down"?"bg-red-100 text-red-500":"text-slate-300 hover:bg-slate-100 hover:text-slate-500"}`}
 >
 <ThumbsDown className="h-3 w-3"/>
 </button>
 </div>
 )}
 </div>
 </div>
 );
 })}
 {typing && <TypingIndicator />}
 </div>

 {/* Chip categories + chips */}
 <div className="border-t border-slate-200 bg-white px-3 py-2">
 {/* Category tabs */}
 <div className="mb-1.5 flex gap-1">
 {chips.map((cat, idx) => (
 <button
 key={cat.label}
 type="button"onClick={() => setChipCategory(idx)}
 className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition ${
 chipCategory === idx
 ?"bg-emerald-50 text-role-primary ring-1 ring-inset ring-emerald-200":"text-slate-400 hover:text-slate-600"}`}
 >
 {cat.label}
 </button>
 ))}
 </div>
 {/* Chips */}
 <div className="flex flex-wrap gap-1.5">
 {currentChips?.chips.map((c) => (
 <button
 key={c}
 type="button"onClick={() => handleChip(c)}
 className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition hover:-translate-y-0.5 hover:shadow-sm active:scale-95 ${theme.chipBg} ${theme.chipTxt} ${theme.chipRing}`}
 >
 <Sparkles className="h-3 w-3"/>
 {c}
 </button>
 ))}
 <button
 type="button"onClick={() => handleChip("help")}
 className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-300 transition hover:-translate-y-0.5 active:scale-95"title="See every topic I cover">
 📚 All Topics
 </button>
 </div>
 </div>

 {/* Input */}
 <form
 onSubmit={handleSubmit}
 className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
 <input
 ref={inputRef}
 value={draft}
 onChange={(e) => setDraft(e.target.value)}
 placeholder="Ask me anything — try`help`"aria-label="Type your question"data-user-id={userId}
 className="flex-1 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200"/>
 <button
 type="submit"aria-label="Send"disabled={!draft.trim() || typing}
 className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition active:scale-90 disabled:cursor-not-allowed disabled:opacity-50 ${theme.sendBg}`}
 >
 <Send className="h-3.5 w-3.5"/>
 </button>
 </form>
 </div>
 )}

 {/* Floating open button removed — chatbot is opened via navbar ? icon */}
 </>
 );
}

